import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link } from '@tanstack/react-router';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { modulesUseContractBaseline } from '@/components/engagement/module-cards';
import {
  isRunInFlight,
  useBaseFormationRunMutation,
  useLatestBaseFormationRunQuery,
} from '@/lib/api/base-formation';
import { useEngagementQuery } from '@/lib/api/engagements';

/**
 * "Run AI extraction" — re-runs base formation (E03-F01) over the
 * engagement's indexed baseline documents on demand. Until now only the
 * new-engagement wizard could start a run, so an engagement created before
 * a better extraction shipped had no way to be reprocessed.
 *
 * The control reads the engagement's latest run from the server (not a run
 * id held in component state), so a run that is already going, or ended
 * degraded or failed, is shown after a reload too:
 *   - queued / running: a spinner with the sections read so far;
 *   - succeeded: a link to the proposals, and "Run again" (a revised SOW,
 *     or a better extraction, is read by running again);
 *   - degraded / failed: "Run again" (the banner above the library says why).
 * Every start mints a fresh Idempotency-Key, so a retry creates a new run
 * instead of replaying the one that failed. The API allows one run in
 * flight per engagement and returns the existing run when one is already
 * queued or running. Starting needs engagement_lead; a consultant sees the
 * API's authorization error.
 *
 * An engagement with no IV&V module (Discovery and/or Selection only) has
 * no contract baseline to form: the API refuses a run for it
 * (`details.reason = 'module_off'`), so the button is replaced by a plain
 * note instead of offering a run that would be refused. Until the
 * engagement has loaded the button shows as before.
 */
export const EXTRACTION_OFF_NOTE = 'No AI extraction: this engagement has no IV&V module';
export const EXTRACTION_OFF_DETAIL =
  'AI extraction reads the contract baseline (SOW, master agreement, plans), which only Contract Compliance, Health Check and Go-Live Readiness engagements have. Discovery and Selection work from their own screens.';

export function RunExtractionButton({ engagementId }: { engagementId: string }) {
  const [confirming, setConfirming] = useState(false);
  const start = useBaseFormationRunMutation();
  const latest = useLatestBaseFormationRunQuery(engagementId);
  const engagement = useEngagementQuery(engagementId);
  const run = latest.data ?? null;
  const status = run?.status;
  const running = isRunInFlight(status);
  const modulesEnabled = engagement.data?.modules_enabled;
  const baselineOff = modulesEnabled !== undefined && !modulesUseContractBaseline(modulesEnabled);

  async function begin() {
    try {
      await start.mutateAsync({ engagementId, idempotencyKey: crypto.randomUUID() });
      setConfirming(false);
    } catch {
      // Surfaced through the mutation's error state in the dialog.
    }
  }

  if (baselineOff && !running) {
    return (
      <span
        role="note"
        title={EXTRACTION_OFF_DETAIL}
        className="inline-flex h-8 items-center rounded-card border border-ink-100 bg-paper-50 px-3 text-meta text-ink-500"
      >
        {EXTRACTION_OFF_NOTE}
      </span>
    );
  }

  return (
    <>
      {running ? (
        <span className="inline-flex h-8 items-center gap-2 rounded-card border border-ai-surface-border bg-ai-surface px-3 text-meta font-medium text-ai-ink">
          <Spinner />
          {status === 'queued'
            ? 'Extraction queued'
            : run?.progress
              ? `Reading ${run.progress.done} of ${run.progress.total} sections`
              : 'Extracting…'}
        </span>
      ) : (
        <>
          {status === 'succeeded' ? (
            <Link
              to="/engagements/$engagementId/proposals"
              params={{ engagementId }}
              className="inline-flex h-8 items-center gap-2 rounded-card border border-ai-surface-border bg-ai-surface px-3 text-meta font-medium text-ai-ink hover:underline"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {run?.proposal_count ?? 0} proposals ready to review
            </Link>
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(true)}>
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {status === undefined ? 'Run AI extraction' : 'Run again'}
          </Button>
        </>
      )}

      <Dialog.Root open={confirming} onOpenChange={setConfirming}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed left-1/2 top-1/2 z-50 w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-modal bg-paper-0 shadow-modal focus:outline-none"
          >
            <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-6 pb-4 pt-5">
              <Dialog.Title className="text-h2 font-semibold text-ink-900">
                Run AI extraction
              </Dialog.Title>
              <Dialog.Close
                aria-label="Close"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-card border border-ink-200 text-ink-700 hover:bg-paper-100"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Dialog.Close>
            </div>
            <div className="space-y-3 px-6 py-5 text-body-sm text-ink-700">
              <p>
                The AI reads every page of this engagement’s contract documents and proposes the
                baseline: deliverables, milestones, payment terms, KPIs, SLAs, IV&amp;V checkpoints
                and the methodology. Each proposal waits for your review.
              </p>
              <p>
                Items you already approved are not changed, but the AI may propose them again. A
                long contract takes several minutes. If the AI service is busy, the run waits and
                retries on its own; a section it still cannot read is skipped, what was read is
                kept, and you can run again for the rest.
              </p>
              {start.error ? <ErrorMessage error={start.error} /> : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-paper-50 px-6 py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={start.isPending}
                onClick={() => void begin()}
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              >
                {start.isPending ? (
                  <Spinner />
                ) : (
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                )}
                Run extraction
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
